import type { User } from '~/domains/user/User.types'
import type { Post } from '~/domains/post/Post.types'
import type { UserProfileUpdateValues } from '~/domains/user/User.types'
import type { DiaryNotificationsEnum } from '~/enums/user/DiaryNotificationsEnum'
import type ToastInterface from '~/types/app/store/ToastInterface'
import { UserComplaintReasonId } from '~/domains/user/UserComplaintReasonIdEnum'
import { ToastTypesEnum } from '~/types/app/store/ToastTypesEnum'
import { useUserRepository } from '~/domains/user/UserRepository'

// Узкий port записи профиля — сборка port вынесена в src/platform/user/UserPortsAdapter.ts.
export type UserProfileUpdatePort = {
  setInfo: (userId: User['id'], values: UserProfileUpdateValues) => Promise<void>
}

// Узкий port резолвящих методов сервиса — сборка port вынесена в
// src/platform/user/UserStorePortsAdapter.ts. auth.user входит ради getUser:
// DraftService.fetchDraft форвардит этот же port в UserService.getUser целиком.
export type UserStorePorts = {
  auth: {
    userIdOrNull: User['id'] | null
    user: User | null
  }
  diary: {
    currentUser: User | null
  }
  toasts: {
    addToast: (toast: ToastInterface, options?: { atStart?: boolean }) => void
  }
}

// Узкий port для getUser: только чтение auth/diary, toasts не участвует. Кроме
// DraftService (через UserStorePorts) собирается инлайн и в BlogService/MessagerService
// (`{ auth, diary }` из их собственных портов) — состав обязан оставаться подмножеством
// auth/diary любого порта, который его форвардит.
export type UserLookupStorePorts = {
  auth: {
    user: User | null
  }
  diary: {
    currentUser: User | null
  }
}

export function useUserService () {
  const UserRepository = useUserRepository()

  const userComplain = async ({ diaryId, complaintId, post }: {
    diaryId: number
    complaintId: UserComplaintReasonId
    post?: Post | null
  }, ports: UserStorePorts) => {
    const { auth: authStore, toasts: toastsStore } = ports

    try {
      await UserRepository.addUserComplain({
        userId: authStore.userIdOrNull!,
        userSuspectId: diaryId,
        complaintId: complaintId,
        postId: post?.id
      })

      toastsStore.addToast({
        type: ToastTypesEnum.info,
        content: 'Жалоба была отправлена'
      })
    } catch (error) {
      toastsStore.addToast({
        type: ToastTypesEnum.error,
        content: `Ошибка при отправке жалобы: ${error.message}`
      })
    }
  }

  const addToBlacklist = async (userIdAdd: number, ports: UserStorePorts) => {
    const { auth: authStore, toasts: toastsStore } = ports

    try {
      await UserRepository.addUserToBlacklist({
        userId: authStore.userIdOrNull!,
        userIdAdd
      })
      toastsStore.addToast({
        type: ToastTypesEnum.info,
        content: `Пользователь добавлен в чёрный список (${userIdAdd})`
      })
    } catch (error) {
      if (error.code === 501) {
        toastsStore.addToast({
          type: ToastTypesEnum.error,
          content: `Пользователь уже есть в чёрном списке (${userIdAdd})`
        })
      } else {
        toastsStore.addToast({
          type: ToastTypesEnum.error,
          content: `Ошибка при отправке: ${error.message}`
        })
      }
    }
  }

  // Тонкий делегат без побочных эффектов (toast/ошибки) — для страниц, которые сами
  // решают, что показать пользователю по результату (settings/blacklist.vue).
  const getBlacklist = (userId: User['id']): Promise<User['id'][]> => {
    return UserRepository.getBlacklist(userId)
  }

  const removeFromBlacklist = (userId: User['id'], userIdRemove: User['id']): Promise<void> => {
    return UserRepository.removeFromBlacklist({ userId, userIdRemove })
  }

  const addUserToBlacklist = (userId: User['id'], userIdAdd: User['id']): Promise<void> => {
    return UserRepository.addUserToBlacklist({ userId, userIdAdd })
  }

  const getUser = async ({ id, login }: {
    id?: string | number
    login?: string
  }, ports: UserLookupStorePorts): Promise<User | null> => {
    const { auth: authStore, diary: diaryStore } = ports

    if (
      authStore.user &&
      (authStore.user.id === id || authStore.user.login === login)
    ) {
      return authStore.user
    }

    if (
      diaryStore.currentUser &&
      (diaryStore.currentUser.id === id || diaryStore.currentUser.login === login)
    ) {
      return diaryStore.currentUser
    }

    if (id) {
      return UserRepository.getById(id)
    }

    if (login) {
      const { user } = await UserRepository.getByLogin(login)

      return user
    }

    return null
  }

  const fetchDiaryActivity = async (ports: UserStorePorts) => {
    const { auth: authStore, diary: diaryStore } = ports

    const activityList = await UserRepository.getActivity({
      requestUserId: authStore.userIdOrNull,
      userId: diaryStore.currentUser!.id
    })

    return activityList
      .filter(({ type }) => [1, 2, 5, 6].includes(type))
  }

  const deleteDiary = async (ports: UserStorePorts): Promise<void> => {
    const { diary: diaryStore, auth: authStore, toasts: toastsStore } = ports

    if (!diaryStore.currentUser) {
      return
    }

    if (!confirm(`Удалить дневник пользователя ${diaryStore.currentUser.fio}?`)) return

    await UserRepository.deleteUser(
      diaryStore.currentUser.id,
      `Дневник пользователя с id: ${diaryStore.currentUser.id} удалён администратором с id: ${authStore.userIdOrNull!}`
    )

    toastsStore.addToast({
      type: ToastTypesEnum.success,
      content: `Дневник пользователя с id: ${diaryStore.currentUser.id} успешно удалён`
    })
  }

  const setDiaryUserLabel = async (label: string, ports: UserStorePorts) => {
    const { diary: diaryStore, toasts: toastsStore } = ports

    if (!diaryStore.currentUser) {
      return
    }

    await UserRepository.setUserLabel({
      label,
      userId: diaryStore.currentUser.id
    })

    toastsStore.addToast({
      type: ToastTypesEnum.success,
      content: 'Заметка сохранена'
    })
  }

  const deleteDiaryUserLabel = async (ports: UserStorePorts) => {
    const { diary: diaryStore, toasts: toastsStore } = ports

    if (!diaryStore.currentUser) {
      return
    }

    await UserRepository.deleteUserLabel(diaryStore.currentUser.id)

    toastsStore.addToast({
      type: ToastTypesEnum.success,
      content: 'Заметка удалена'
    })
  }

  const updateProfile = (
    userId: User['id'],
    values: UserProfileUpdateValues,
    ports: UserProfileUpdatePort
  ): Promise<void> => {
    return ports.setInfo(userId, values)
  }

  const checkCurrentPassword = (userId: User['id'], password: string): Promise<boolean> => {
    return UserRepository.checkPassword(userId, password)
  }

  const checkLoginAvailability = (login: string): Promise<boolean> => {
    return UserRepository.checkLoginAvailable(login)
  }

  // Удаление собственного дневника (настройки, с подтверждением паролем) — отличается
  // от deleteDiary выше, который удаляет ЧУЖОЙ дневник модератором.
  const deleteOwnAccount = (userId: User['id'], reason: string): Promise<void> => {
    return UserRepository.deleteUser(userId, reason)
  }

  // Тонкие делегаты без побочных эффектов — страница сама решает, что показать
  // пользователю по результату (settings/notify-setting.vue, pages/doctors/index.vue,
  // components/popular/PopularMixin.ts).
  const getNotificationsSettings = (
    userId: User['id']
  ): Promise<Record<DiaryNotificationsEnum, boolean>> => {
    return UserRepository.getNotificationsSettings(userId)
  }

  const setNotificationsSettings = (
    userId: User['id'],
    params: Record<DiaryNotificationsEnum, number>
  ): Promise<void> => {
    return UserRepository.setNotificationsSettings(userId, params)
  }

  const search = (params: {
    name?: string | (string | null)[]
    isDoctor?: number
    doctorSpeciality?: string | (string | null)[] | number
    page?: string | (string | null)[] | number
    perPage?: number
  }) => {
    return UserRepository.search(params)
  }

  const getPopularUsers = (params: {
    userId: User['id']
    page?: number
    perPage?: number
  }) => {
    return UserRepository.getPopularUsers(params)
  }

  return {
    userComplain,
    addToBlacklist,
    getBlacklist,
    removeFromBlacklist,
    addUserToBlacklist,
    getUser,
    fetchDiaryActivity,
    deleteDiary,
    setDiaryUserLabel,
    deleteDiaryUserLabel,
    updateProfile,
    checkCurrentPassword,
    checkLoginAvailability,
    deleteOwnAccount,
    getNotificationsSettings,
    setNotificationsSettings,
    search,
    getPopularUsers
  }
}
